import { colors } from '@ardoq/design-tokens';
import {
  APIEntityType,
  FilterInfoOperator,
  FieldFormattingVersion,
  FormattingOperator,
  RelativeDatePeriod,
  RelativeDateUnit,
  type RelativeDateValue,
  CommonDefaultFilterKeys,
} from '@ardoq/api-types';
import {
  ComponentOrReferenceFormattingRule,
  Formatting,
  FormattingRule,
  RuleType,
} from './types';
import { v4 as uuidv4 } from 'uuid';
import { QueryBuilderFilterType } from '@ardoq/query-builder';
import {
  dateComparators,
  idMatchComparators,
  standardComparators,
} from './composeFormattingGroups/filteringComparators';
import { SelectOptionWithDescription } from './composeFormattingGroups/utils';
import { type I18nLocale, t } from '@ardoq/i18n';

type DefaultProps = { field: string; comparator: FilterInfoOperator };

export type BuiltInFieldDef = {
  id: string;
  label: (i18nLocale: I18nLocale, isNew: boolean) => string;
  filterType: QueryBuilderFilterType;
  comparators: FormattingOperator[];
  selectOptions?: (args: {
    options: SelectOptionWithDescription[];
  }) => SelectOptionWithDescription[];
};

export const toBuiltInFieldFormatting = (
  def: BuiltInFieldDef,
  label: string,
  typeOptions: SelectOptionWithDescription[]
): Formatting => ({
  id: def.id,
  label,
  filterType: def.filterType,
  comparators: def.comparators,
  ...(def.selectOptions
    ? { selectOptions: def.selectOptions({ options: typeOptions }) }
    : {}),
});

const DEFAULT_COMPONENT_PROPS: DefaultProps = {
  field: 'name',
  comparator: FilterInfoOperator.EQUALS,
};
const DEFAULT_REFERENCE_PROPS: DefaultProps = {
  field: 'type',
  comparator: FilterInfoOperator.ID_MATCH,
};

const DEFAULT_PROPS_LOOKUP: Record<
  APIEntityType.COMPONENT | APIEntityType.REFERENCE,
  DefaultProps
> = {
  [APIEntityType.COMPONENT]: DEFAULT_COMPONENT_PROPS,
  [APIEntityType.REFERENCE]: DEFAULT_REFERENCE_PROPS,
};

export const getEmptyRuleByType = (
  type: RuleType,
  isNewConditionalFormatting: boolean
): FormattingRule => {
  const defaultRuleTypeValues = {
    __id: uuidv4(),
    isInverted: false,
    color: colors.yellow700,
    ...(isNewConditionalFormatting
      ? { version: FieldFormattingVersion.NEW_CONDITIONAL_FORMATTING }
      : { version: FieldFormattingVersion.ORIGINAL }),
  };

  if (type === APIEntityType.TAG) {
    return {
      ...defaultRuleTypeValues,
      type: APIEntityType.TAG,
      value: undefined,
    };
  }
  return {
    ...defaultRuleTypeValues,
    type,
    value: null,
    field: isNewConditionalFormatting ? null : DEFAULT_PROPS_LOOKUP[type].field,
    comparator: DEFAULT_PROPS_LOOKUP[type].comparator,
    typeNames: null,
  };
};
/** When the field is TYPE and exactly one type is selected, the value must equal that type name. */
export const getValueForTypeField = (
  typeNames: string[] | null
): string | null => (typeNames?.length === 1 ? typeNames[0] : null);

// TYPE field can be selected only when exactly one type name is available.
export const canSetTypeField = (
  field: string,
  typeNames: string[] | null
): boolean =>
  field !== CommonDefaultFilterKeys.TYPE ||
  getValueForTypeField(typeNames) !== null;

export const getAllTypesLabel = (
  i18nLocale: I18nLocale,
  type: ComponentOrReferenceFormattingRule['type']
): string =>
  type === APIEntityType.COMPONENT
    ? t(
        i18nLocale,
        'perspectives:formattingRuleTypePicker.allComponentTypes',
        'All component types'
      )
    : t(
        i18nLocale,
        'perspectives:formattingRuleTypePicker.allReferenceTypes',
        'All reference types'
      );

export const getTypeHeaderLabel = (
  i18nLocale: I18nLocale,
  type: ComponentOrReferenceFormattingRule['type']
): string =>
  type === APIEntityType.COMPONENT
    ? t(
        i18nLocale,
        'perspectives:formattingRuleTypePicker.componentTypeHeader',
        'Component type'
      )
    : t(
        i18nLocale,
        'perspectives:formattingRuleTypePicker.referenceTypeHeader',
        'Reference type'
      );

/** @returns true unless comparator is boolean. */
export const showInvertCheckbox = (comparator: FilterInfoOperator | string) =>
  comparator !== FilterInfoOperator.BOOLEAN;

/** Retrieves the label of a field from formatting configuration or returns the field name if no label is set. */
export const getFieldLabelOrName = (
  formattingByRule: Partial<Record<string, Formatting>>,
  field: string
): string => {
  const formatting = formattingByRule[field];
  const raw = formatting && formatting.label ? formatting.label : field;

  // Truncate to max 35 chars, adding ellipsis if needed
  const MAX_LEN = 35;
  if (raw.length > MAX_LEN) {
    return `${raw.slice(0, MAX_LEN - 1)}…`;
  }

  return raw;
};

export const COMPONENT_BUILT_IN_FIELD_DEFS: BuiltInFieldDef[] = [
  {
    id: 'name',
    label: i18nLocale =>
      t(i18nLocale, 'perspectives:builtInFields.name', 'Name'),
    filterType: QueryBuilderFilterType.ASYNC_COMPONENT_NAME,
    comparators: standardComparators,
  },
  {
    id: 'description',
    label: i18nLocale =>
      t(i18nLocale, 'perspectives:builtInFields.description', 'Description'),
    filterType: QueryBuilderFilterType.STRING,
    comparators: standardComparators,
  },
  {
    id: 'type',
    label: i18nLocale =>
      t(i18nLocale, 'perspectives:builtInFields.type', 'Type'),
    filterType: QueryBuilderFilterType.SELECT,
    comparators: idMatchComparators,
    selectOptions: ({ options }) => options,
  },
  {
    id: '_id',
    label: i18nLocale =>
      t(i18nLocale, 'perspectives:builtInFields.ardoqOid', 'Ardoq OID'),
    filterType: QueryBuilderFilterType.ASYNC_COMPONENT_OID,
    comparators: idMatchComparators,
  },
  {
    id: 'parent',
    label: (i18nLocale, isNew) =>
      isNew
        ? t(i18nLocale, 'perspectives:builtInFields.parent', 'Parent')
        : t(i18nLocale, 'perspectives:builtInFields.childrenOf', 'Children of'),
    filterType: QueryBuilderFilterType.ASYNC_PARENT,
    comparators: idMatchComparators,
  },
  {
    id: '_version',
    label: i18nLocale =>
      t(i18nLocale, 'perspectives:builtInFields.revision', 'Revision'),
    filterType: QueryBuilderFilterType.NUMBER,
    comparators: standardComparators,
  },
  {
    id: 'created',
    label: i18nLocale =>
      t(i18nLocale, 'perspectives:builtInFields.created', 'Created'),
    filterType: QueryBuilderFilterType.DATE_TIME,
    comparators: dateComparators,
  },
  {
    id: 'last-updated',
    label: i18nLocale =>
      t(i18nLocale, 'perspectives:builtInFields.lastUpdated', 'Last updated'),
    filterType: QueryBuilderFilterType.DATE_TIME,
    comparators: dateComparators,
  },
  {
    id: 'incomingRefCount',
    label: i18nLocale =>
      t(
        i18nLocale,
        'perspectives:builtInFields.incomingReferences',
        'Incoming references'
      ),
    filterType: QueryBuilderFilterType.NUMBER,
    comparators: standardComparators,
  },
  {
    id: 'outgoingRefCount',
    label: i18nLocale =>
      t(
        i18nLocale,
        'perspectives:builtInFields.outgoingReferences',
        'Outgoing references'
      ),
    filterType: QueryBuilderFilterType.NUMBER,
    comparators: standardComparators,
  },
];

export const REFERENCE_BUILT_IN_FIELD_DEFS: BuiltInFieldDef[] = [
  {
    id: 'displayText',
    label: i18nLocale =>
      t(i18nLocale, 'perspectives:builtInFields.displayText', 'Display text'),
    filterType: QueryBuilderFilterType.STRING,
    comparators: standardComparators,
  },
  {
    id: 'description',
    label: i18nLocale =>
      t(i18nLocale, 'perspectives:builtInFields.description', 'Description'),
    filterType: QueryBuilderFilterType.STRING,
    comparators: standardComparators,
  },
  {
    id: 'type',
    label: i18nLocale =>
      t(i18nLocale, 'perspectives:builtInFields.type', 'Type'),
    filterType: QueryBuilderFilterType.SELECT,
    selectOptions: ({ options }) => options,
    comparators: idMatchComparators,
  },
  {
    id: '_id',
    label: i18nLocale =>
      t(i18nLocale, 'perspectives:builtInFields.ardoqOid', 'Ardoq OID'),
    filterType: QueryBuilderFilterType.ASYNC_REFERENCE_OID,
    comparators: idMatchComparators,
  },
  {
    id: 'created',
    label: i18nLocale =>
      t(i18nLocale, 'perspectives:builtInFields.created', 'Created'),
    filterType: QueryBuilderFilterType.DATE_TIME,
    comparators: dateComparators,
  },
  {
    id: 'last-updated',
    label: i18nLocale =>
      t(i18nLocale, 'perspectives:builtInFields.lastUpdated', 'Last updated'),
    filterType: QueryBuilderFilterType.DATE_TIME,
    comparators: dateComparators,
  },
  {
    id: 'target',
    label: i18nLocale =>
      t(i18nLocale, 'perspectives:builtInFields.referenceTo', 'Reference to'),
    filterType: QueryBuilderFilterType.ASYNC_TARGET,
    comparators: idMatchComparators,
  },
  {
    id: 'source',
    label: i18nLocale =>
      t(
        i18nLocale,
        'perspectives:builtInFields.referenceFrom',
        'Reference from'
      ),
    filterType: QueryBuilderFilterType.ASYNC_SOURCE,
    comparators: idMatchComparators,
  },
  {
    id: 'targetWorkspace',
    label: i18nLocale =>
      t(
        i18nLocale,
        'perspectives:builtInFields.showReferencesToWorkspace',
        'Show references to workspace'
      ),
    filterType: QueryBuilderFilterType.ASYNC_ROOT_WORKSPACE,
    comparators: idMatchComparators,
  },
  {
    id: 'rootWorkspace',
    label: i18nLocale =>
      t(
        i18nLocale,
        'perspectives:builtInFields.showReferenceFromWorkspace',
        'Show reference from workspace'
      ),
    filterType: QueryBuilderFilterType.ASYNC_ROOT_WORKSPACE,
    comparators: idMatchComparators,
  },
];

export const COMPONENT_BUILT_IN_FIELDS: Record<string, QueryBuilderFilterType> =
  Object.fromEntries(
    COMPONENT_BUILT_IN_FIELD_DEFS.map(d => [d.id, d.filterType])
  );

export const REFERENCE_BUILT_IN_FIELDS: Record<string, QueryBuilderFilterType> =
  Object.fromEntries(
    REFERENCE_BUILT_IN_FIELD_DEFS.map(d => [d.id, d.filterType])
  );

const RELATIVE_DATE_PERIOD_LABELS = {
  [RelativeDatePeriod.THIS]: 'This',
  [RelativeDatePeriod.LAST]: 'Last',
  [RelativeDatePeriod.NEXT]: 'Next',
  [RelativeDatePeriod.IN_THE_NEXT]: 'In the next',
  [RelativeDatePeriod.IN_THE_PAST]: 'In the past',
};

const RELATIVE_DATE_UNIT_LABELS = {
  [RelativeDateUnit.DAY]: 'day',
  [RelativeDateUnit.WEEK]: 'week',
  [RelativeDateUnit.MONTH]: 'month',
  [RelativeDateUnit.QUARTER]: 'quarter',
  [RelativeDateUnit.YEAR]: 'year',
};

/**
 * Formats a RelativeDateValue into a human-readable string.
 * @example
 * formatRelativeDateValue({ period: RelativeDatePeriod.THIS, unit: RelativeDateUnit.WEEK }) // "This week"
 */
export const formatRelativeDateValue = (value: RelativeDateValue): string => {
  const periodLabel = RELATIVE_DATE_PERIOD_LABELS[value.period] ?? value.period;
  const unitLabel = RELATIVE_DATE_UNIT_LABELS[value.unit] ?? value.unit;

  if ('amount' in value) {
    // "In the next 5 days" or "In the past 3 weeks"
    const pluralUnit = value.amount === 1 ? unitLabel : `${unitLabel}s`;
    return `${periodLabel} ${value.amount} ${pluralUnit}`;
  }

  // "This week", "Last month", "Next quarter"
  return `${periodLabel} ${unitLabel}`;
};

export const normalizeTypeNames = (
  selectedTypeNames: string[] | null
): string[] | null =>
  selectedTypeNames && selectedTypeNames.length > 0 ? selectedTypeNames : null;

export const getTypePickerUpdate = (
  newTypeNames: string[] | null,
  field: ComponentOrReferenceFormattingRule['field']
): Partial<ComponentOrReferenceFormattingRule> => {
  if (field !== CommonDefaultFilterKeys.TYPE) {
    return { typeNames: newTypeNames };
  }
  if (newTypeNames?.length !== 1) {
    return { typeNames: newTypeNames, field: null };
  }
  return { typeNames: newTypeNames, value: newTypeNames[0] };
};
